<template>
  <div v-if="uploaderOpen" class="dropzone ui segment">
    <media-uploader
      ref="mediaUploader"
      :max-uploads="250"
      @uploads="updateUploads"
      @progress="totalProgress = $event"
      v-slot:default="slotProps"
    >
      <div style="margin-top: 16px">
        <drive-uploader-form
          :uploads-array="uploads"
          :parent="parent"
          @formData="appendFormDataToUploads"
          @s3Key="appendS3KeyToUploads"
          @change="slotProps.validate"
          @remove="(token) => removeUpload(token)"
        ></drive-uploader-form>
        <drive-uploader-submit
          :saving="saving"
          :saved="saved"
          :processedUploads="processedUploads"
          :totalUploads="totalUploads"
          :uploadsArray="uploads"
          :uploadsCount="uploads.length"
          :savedUploads="savedUploads"
          :failedUploads="errorUploads"
          :disabled="!slotProps.valid"
          @submit="submit(slotProps.parallelUploads, slotProps.valid)"
        ></drive-uploader-submit>
      </div>
    </media-uploader>
    <upload-progress
      v-if="uploads.length > 0"
      :total-progress="totalProgress"
      :saving="saving"
    ></upload-progress>
  </div>
</template>

<script lang="ts">
import vue2Dropzone from "vue2-dropzone"
import DriveUploadComponent from "./DriveUpload.vue"
import DriveUploaderSubmit from "components/Drive/DriveUploaderSubmit.vue"
import { createNamespacedHelpers } from "vuex"
import DriveUploaderForm from "components/Drive/DriveUploaderForm.vue"
import UploadProgress from "components/Drive/UploadProgress.vue"
import MediaUploader, {
  MediaUploaderType,
} from "features/media/MediaUploader.vue"
import { MediaUpload } from "features/media/media.types"
import { defineComponent, PropType } from "vue"
import { DriveInode, DriveUpload } from "components/Drive/drive.types"

const { mapState, mapMutations, mapActions } = createNamespacedHelpers("drive")

export default defineComponent({
  components: {
    MediaUploader,
    DriveUploadComponent,
    DriveUploaderSubmit,
    DriveUploaderForm,
    vueDropzone: vue2Dropzone,
    UploadProgress,
  },
  props: {
    parent: {
      type: Object as PropType<DriveInode>,
    },
    showSuccess: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    saving(): boolean {
      return (
        this.totalUploads > 0 && this.totalUploads !== this.processedUploads
      )
    },
    saved(): boolean {
      return (
        this.totalUploads > 0 && this.totalUploads === this.processedUploads
      )
    },
    updateUpload(): (obj: Partial<DriveUpload>) => (token: string) => void {
      return (this.$refs.mediaUploader as any).updateUpload
    },
    ...mapState(["uploaderOpen"]),
  },
  data() {
    return {
      totalUploads: 0,
      processedUploads: 0,
      savedUploads: 0,
      errorUploads: 0,
      uploadsInProgress: 0,
      uploads: [] as DriveUpload[],
      totalProgress: 0,
    }
  },
  methods: {
    getUpload(token: string): DriveUpload {
      return this.uploads.find((e) => e.file.token === token)
    },
    appendFormDataToUploads(formData) {
      for (const upload of this.uploads) {
        const fd = formData[upload.file.token]
        if (fd) upload.formData = fd
      }
    },
    appendS3KeyToUploads({ s3Key, token }: { s3Key: string; token: string }) {
      const upload = this.getUpload(token)
      upload.s3Key = s3Key
      this.updateUpload({ s3Key })(token)
    },
    removeUpload(token) {
      const upload = this.uploads.find((e) => e.file.token === token)
      if (upload) {
        if (upload.errors.length > 0) this.errorUploads--
        ;(this.$refs.mediaUploader as any).removeUpload(token)
      }
    },
    updateUploads(uploads: MediaUpload[]) {
      this.uploads = uploads.map((mediaUpload) => {
        const availableUpload = this.uploads.find(
          (upload) => upload.file.token === mediaUpload.file.token
        )
        return availableUpload
          ? {
              ...availableUpload,
              ...mediaUpload,
              formData: availableUpload.formData, // avoid that formData is overriden
            }
          : (mediaUpload as DriveUpload)
      })
    },
    submit(parallelUploads, valid) {
      if (!valid) return
      this.totalUploads = this.uploads.length
      this.processedUploads = 0
      this.savedUploads = 0
      this.errorUploads = 0
      const uploadQueue = this.uploads.map((e) => e.file.token)
      this.upload(parallelUploads, uploadQueue)
    },
    async upload(parallelUploads, uploadQueue) {
      while (uploadQueue.length > 0) {
        if (this.uploadsInProgress <= parallelUploads) {
          const fileToken = uploadQueue.shift()
          this.saveUpload(fileToken)
        } else {
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }
    },
    appendParentId(formData) {
      formData.inode.parent_id = this.parent?.id
      return formData
    },
    async saveUpload(token) {
      this.uploadsInProgress++
      const upload = this.uploads.find((e) => e.file.token === token)

      try {
        await this.createFile(this.appendParentId(upload.formData))
        this.savedUploads++
        ;(this.$refs.mediaUploader as MediaUploaderType).removeUpload(token)
      } catch (err: any) {
        this.handleUploadError(err, token)
      } finally {
        this.processedUploads++
        this.uploadsInProgress--
      }
    },
    handleUploadError(err: any, token: string) {
      this.errorUploads++
      if (err.response) {
        // The request was made and the server responded with a
        // status code that falls out of the range of 2xx
        try {
          this.updateUpload({ errors: err.response.data })(token)
        } catch (e) {
          this.updateUpload({ errors: ["Unerwartete Antwort des Servers."] })(
            token
          )
        }
      } else if (err.request) {
        // The request was made but no response was received, `err.request`
        // is an instance of XMLHttpRequest
        this.updateUpload({ errors: ["Keine Antwort vom Server."] })(token)
      } else {
        console.error(err)
        // Something happened in setting up the request and triggered an Error
        this.updateUpload({
          errors: ["Fehler beim Vorbereiten der Serveranfrage."],
        })(token)
      }
    },
    reset() {
      this.totalUploads = 0
      this.processedUploads = 0
      this.uploads = []
      this.savedUploads = 0
      this.errorUploads = 0
    },
    ...mapMutations(["showUploader"]),
    ...mapActions(["createFile"]),
  },
  watch: {
    uploaderOpen(open) {
      if (!open) {
        ;(this.$refs.mediaUploader as any).reset()
        this.reset()
      }
    },
  },
})
</script>

<style lang="sass">
#driveUploader
  border: #307EF7 3px dashed
  background: white
  padding: 0
  height: 100%
  min-height: 200px
  display: flex

  .ui.progress
    color: transparent

  .dz-message
    width: 100%
    display: flex
    justify-content: center
    margin: auto
    text-align: center
    font-size: 1.25rem
    flex-direction: column
    height: 100%
</style>
